import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { Link, NavLink } from 'react-router-dom';

import { Tab, Tabs, ContentHeader, LoaderWrapper, Panels, Panel, Pagination } from '@tovia/man-ui';
import { useHistory, useParams } from 'react-router';
import PaginationModal from '../../components/modals/PaginationModal';
import { load } from '../../redux/modules/specialFeatures';
import { GalleryCards } from '../Cards/Gallery/GalleryCards';
import { useSelector } from '../../redux/modules/helpers/useSelector';
import urls from 'src/shared/urls';

const subtitle = {
  Director_Picks: 'Only the best hand picked sets updated daily.',
  Casting_Calls: 'Real casting calls... the good, the bad, the gorgeous!',
  Productions: 'A rare look behind the scenes.',
  Vacations: "Traveling to the World's most exotic locations.",
};

const Div = styled.div`
  &.special-features {
    .category-title {
      margin: 24px 12px 0;

      .category-title-site {
        color: ${(props) => props.theme.splashPrimary2};
        font-size: 3.4rem;
        text-transform: uppercase;

        @media (max-width: 425px) {
          font-size: 2rem;
        }
      }

      .category-title-current {
        color: ${(props) => props.theme.primary9};
        font-size: 3.4rem;
        margin: 0 6px;
        text-transform: lowercase;

        @media (max-width: 425px) {
          font-size: 2rem;
        }
      }

      .category-title-subtitle {
        font-size: 1.72rem;
        margin-left: 16px;

        @media (max-width: 425px) {
          font-size: 1.3rem;
        }

        @media (max-width: 1199px) {
          display: block;
          margin-left: 0;
        }
      }
    }
    .panel-body {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      padding: 4px 15px;

      a {
        flex-grow: 1;
        margin: 0 10px;
      }
    }
    .panels {
      margin-top: 20px;
    }
  }
`;

export default function SpecialFeatures() {
  const specialFeatures = useSelector((state) => state.app.config.specialFeatures);
  const { errorCategories = [], errorCode, galleries, itemCounts, loadingCategories = [] } = useSelector(
    (state) => state.specialFeatures,
  );
  const site = useSelector((state) => state.site);

  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams<{ category: string; subCategory: string; extraCategory: string; page: string }>();

  useEffect(() => {
    if (errorCode === 404) {
      history.replace(urls.get.notFound);
    }
  }, [errorCode, history]);

  const loadParams: {
    searchCategory: string;
    category: string;
    page: number;
    path: string;
  } = useMemo(() => {
    const { category, subCategory, extraCategory, page } = params;
    let searchCategory = category;
    let pageNumber;
    let path = `/category/${category}`;

    const alternateCategoryName = Object.keys(specialFeatures.search).find((cat) => cat === category);

    if (alternateCategoryName) {
      searchCategory = specialFeatures.search[alternateCategoryName];
    }

    // eslint-disable-next-line no-restricted-globals
    if (subCategory && !isNaN(parseInt(subCategory))) {
      pageNumber = parseInt(subCategory);
    } else if (subCategory) {
      searchCategory = subCategory;
      path += `/${subCategory}`;
    }

    // eslint-disable-next-line no-restricted-globals
    if (extraCategory && !isNaN(parseInt(extraCategory))) {
      searchCategory = subCategory;
      pageNumber = parseInt(extraCategory);
    } else if (extraCategory) {
      searchCategory = extraCategory;
      path += `/${extraCategory}`;
    }

    if (page) {
      pageNumber = parseInt(page);
    }

    return {
      searchCategory,
      category,
      page: pageNumber || 1,
      path,
    };
  }, [specialFeatures.search, params]);

  const loadData = useCallback(() => {
    const { searchCategory, page } = loadParams;

    dispatch(
      load({
        category: searchCategory,
        page: page || 1,
      }),
    );
  }, [dispatch, loadParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadParamsPath = loadParams.path;

  const setPage = useCallback(
    (page) => {
      history.push(`${loadParamsPath}/${page}`);
    },
    [history, loadParamsPath],
  );

  const renderContentHeader = () => {
    const { category, subCategory, extraCategory } = params;

    const tabsNode: JSX.Element[] = [];
    if (Object.keys(specialFeatures.subCategories).includes(category)) {
      const tabs = specialFeatures.subCategories[category].map((option) => ({
        value: option,
        name: option.replace(/_/g, ' '),
        to: `/category/${category}/${option}`,
      }));
      tabsNode.push(
        <Tabs key={subCategory} flat>
          {tabs.map((tab) => (
            <Tab key={tab.value} as={Link} active={tab.value === subCategory} {...tab} />
          ))}
        </Tabs>,
      );
    }

    if (Object.keys(specialFeatures.additionalCategories).includes(subCategory)) {
      const tabs = specialFeatures.additionalCategories[subCategory].map((option) => ({
        value: option,
        label: option.replace(/_/g, ' '),
        link: `/category/${category}/${subCategory}/${option}`,
      }));
      tabsNode.push(
        <Panels key={extraCategory}>
          <Panel className="col-md-12">
            <div className="panel-body">
              {tabs.map((tab) => (
                <NavLink key={tab.value} to={tab.link}>
                  {tab.label}
                </NavLink>
              ))}
            </div>
          </Panel>
        </Panels>,
      );
    }

    return <ContentHeader tabs={tabsNode} />;
  };

  const { category, searchCategory, page } = loadParams;
  return (
    <Div className="content container special-features">
      <div className="category-title">
        <span className="category-title-site">{`${site.abbreviation}/`}</span>
        <span className="category-title-current">{category.replace(/_/g, ' ')}</span>
        <span className="category-title-subtitle">{subtitle[category]}</span>
      </div>
      {renderContentHeader()}
      {loadingCategories.includes(searchCategory) ||
      errorCategories.includes(searchCategory) ||
      !galleries[searchCategory] ? (
        <LoaderWrapper
          contentType={category.replace(/_/g, ' ')}
          defaultHeight
          loadingStatus={loadingCategories.includes(searchCategory)}
          reduxErrorStatus={errorCategories.includes(searchCategory) && 'Unable to load category'}
          reloadFunction={loadData}
          waitPeriod={10000}
        />
      ) : (
        <div>
          <GalleryCards galleries={galleries[searchCategory]} />
          <Pagination
            activePage={+(page || 1)}
            boundaryRange={1}
            onPageChange={setPage}
            siblingRange={1}
            totalPages={Math.ceil(itemCounts[searchCategory] / 40)}
            showPrevNext
            pageUrlPrefix={loadParamsPath}
            modal={
              <PaginationModal
                setPage={(pageNumber) => setPage(pageNumber)}
                totalPages={Math.ceil(itemCounts[searchCategory] / 40)}
              />
            }
          />
        </div>
      )}
    </Div>
  );
}
